import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Quote, Instagram, Globe, Music, Star } from "lucide-react";
import ReviewSubmit from "./ReviewSubmit";

const SOURCES = {
  website: { label: "Verified buyer", icon: Globe, color: "text-slate-600" },
  instagram: { label: "Instagram", icon: Instagram, color: "text-pink-600" },
  etsy: { label: "Etsy review", icon: Star, color: "text-orange-600" },
  tiktok: { label: "TikTok", icon: Music, color: "text-fuchsia-600" },
  google: { label: "Google review", icon: Star, color: "text-blue-600" },
};

export default function Testimonials() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/testimonials", { params: { featured: true } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) {
    return (
      <section data-testid="testimonials-section" className="bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-4 py-24 text-center font-modern">
          <h2 className="font-modern text-4xl gold-heading font-extrabold tracking-[-0.04em]">Be the first to leave a review.</h2>
          <p className="mt-4 text-[#5C5547]">Loved something you bought? Tell us — we'd love to share it.</p>
          <div className="mt-8"><ReviewSubmit /></div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="testimonials-section" className="bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="text-center mb-16 font-modern">
          <div className="uppercase text-[11px] tracking-[0.45em] text-[#5C5547] font-medium">Loved by readers, buyers & followers</div>
          <h2 className="mt-5 font-modern text-4xl sm:text-5xl lg:text-7xl gold-heading font-extrabold tracking-[-0.04em]">
            Words from the community.
          </h2>
          <div className="mt-6 mx-auto h-px w-24 gold-line" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 font-modern">
          {items.map((t, i) => {
            const meta = SOURCES[t.source] || SOURCES.website;
            const SrcIcon = meta.icon;
            return (
              <article key={t.id} data-testid={`testimonial-${t.id}`}
                className="group relative fade-up p-8 rounded-[28px] bg-[#FDFBF7] transition hover:-translate-y-1 thick-gold"
                style={{ animationDelay: `${0.05 * i}s` }}>
                <div className="flex items-start justify-between">
                  <Quote className="w-7 h-7 text-[var(--accent-strong)]" />
                  <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F5F2EA] ${meta.color} text-[10px] font-semibold uppercase tracking-[0.15em]`}>
                    <SrcIcon className="w-3 h-3" /> {meta.label}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-0.5 text-[var(--accent-strong)]">
                  {Array.from({ length: t.rating || 5 }).map((_, j) => (
                    <Star key={`${t.id}-star-${j}`} className="w-3.5 h-3.5 fill-current" />
                  ))}
                </div>
                <p className="mt-5 font-modern text-lg leading-snug tracking-[-0.01em] text-[#1A1815] font-medium">
                  "{t.content}"
                </p>
                <div className="mt-7 pt-5 border-t border-[#EAE4D3] flex items-center gap-3">
                  {t.avatar_url && (
                    <img src={t.avatar_url} alt={t.name}
                      className="w-11 h-11 rounded-full object-cover" style={{ border: "2px solid var(--accent-strong)" }} />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm tracking-tight">{t.name}</div>
                    <div className="text-xs text-[#5C5547] mt-0.5">{meta.label}</div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="text-center mt-14">
          <ReviewSubmit />
        </div>
      </div>
    </section>
  );
}
